import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { ArrowLeft, ArrowRight } from 'lucide-react';
import { Link, useSearchParams } from 'react-router-dom';
import WorkCard from '../../components/UI/WorkCard';
import WorkDetailModal, { type WorkDetail } from '../../components/Profile/work/WorkDetailModal';
import { OFFICIAL_STUDIO_PROFILE_PATH } from '../../constants/official';
import {
  STUDIO_INDUSTRIES,
  isStudioIndustry,
  type StudioIndustry,
} from '../../constants/studio';
import { postService, type WorkSummary } from '../../services/postService';
import {
  DEFAULT_OFFICIAL_USER,
  createStudioWorkDetail,
  loadOfficialStudioPortfolio,
  mergeStudioWorkDetail,
  type StudioOfficialUser,
} from './studioPortfolio';
import './Studio.css';
import './StudioIndustries.css';

type IndustryFilter = 'all' | StudioIndustry;

const StudioIndustries = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [officialUser, setOfficialUser] = useState<StudioOfficialUser>(DEFAULT_OFFICIAL_USER);
  const [officialWorks, setOfficialWorks] = useState<WorkSummary[]>([]);
  const [worksLoading, setWorksLoading] = useState(true);
  const [selectedWork, setSelectedWork] = useState<WorkDetail | null>(null);

  const requestedIndustry = searchParams.get('industry');
  const activeIndustry: IndustryFilter = isStudioIndustry(requestedIndustry)
    ? requestedIndustry
    : 'all';

  const industryCounts = useMemo(() => (
    Object.fromEntries(STUDIO_INDUSTRIES.map((industry) => [
      industry.id,
      officialWorks.filter((work) => work.industry === industry.id).length,
    ])) as Record<StudioIndustry, number>
  ), [officialWorks]);

  const filteredWorks = useMemo(() => (
    activeIndustry === 'all'
      ? officialWorks
      : officialWorks.filter((work) => work.industry === activeIndustry)
  ), [activeIndustry, officialWorks]);

  const activeIndustryOption = activeIndustry === 'all'
    ? null
    : STUDIO_INDUSTRIES.find((industry) => industry.id === activeIndustry) || null;

  const setActiveIndustry = (industry: IndustryFilter) => {
    const nextParams = new URLSearchParams(searchParams);
    if (industry === 'all') {
      nextParams.delete('industry');
    } else {
      nextParams.set('industry', industry);
    }
    setSearchParams(nextParams, { replace: true });
  };

  const createDetailFromSummary = useCallback(
    (work: WorkSummary) => createStudioWorkDetail(work, officialUser),
    [officialUser]
  );

  const handleWorkClick = useCallback(async (work: WorkSummary) => {
    setSelectedWork(createDetailFromSummary(work));

    const detail = await postService.getPostDetail(String(work.id));
    if (!detail) return;

    setSelectedWork((current) => {
      if (!current || current.id !== work.id) {
        return current;
      }

      return mergeStudioWorkDetail(current, detail);
    });
  }, [createDetailFromSummary]);

  useEffect(() => {
    let isActive = true;

    const loadWorks = async () => {
      setWorksLoading(true);
      try {
        const portfolio = await loadOfficialStudioPortfolio();
        if (!isActive) return;
        setOfficialUser(portfolio.officialUser);
        setOfficialWorks(portfolio.works);
      } catch (error) {
        console.error('Failed to load studio industry portfolio:', error);
        if (isActive) setOfficialWorks([]);
      } finally {
        if (isActive) setWorksLoading(false);
      }
    };

    void loadWorks();
    window.addEventListener('posts_updated', loadWorks);

    return () => {
      isActive = false;
      window.removeEventListener('posts_updated', loadWorks);
    };
  }, []);

  return (
    <div className="studio-page studio-industries-page">
      <main className="studio-main studio-industries-main">
        <Link className="studio-industries-back" to={OFFICIAL_STUDIO_PROFILE_PATH}>
          <ArrowLeft aria-hidden="true" />
          云作 Studio
        </Link>

        <section className="studio-industry-browser" aria-labelledby="studio-industry-browser-title">
          <div className="studio-industry-browser__heading">
            <div>
              <h2 id="studio-industry-browser-title">从行业语境出发，找到更接近业务目标的表达。</h2>
            </div>
            <span>{officialWorks.length} 项公开案例</span>
          </div>

          <div className="studio-industry-tabs" role="tablist" aria-label="行业案例筛选">
            <button
              type="button"
              role="tab"
              aria-selected={activeIndustry === 'all'}
              className={activeIndustry === 'all' ? 'is-active' : ''}
              onClick={() => setActiveIndustry('all')}
            >
              <span>00</span>
              <strong>全部行业</strong>
              <em>{officialWorks.length}</em>
            </button>
            {STUDIO_INDUSTRIES.map((industry, index) => (
              <button
                key={industry.id}
                type="button"
                role="tab"
                aria-selected={activeIndustry === industry.id}
                className={activeIndustry === industry.id ? 'is-active' : ''}
                onClick={() => setActiveIndustry(industry.id)}
              >
                <span>{String(index + 1).padStart(2, '0')}</span>
                <strong>{industry.label}</strong>
                <em>{industryCounts[industry.id]}</em>
              </button>
            ))}
          </div>

          <div
            className="studio-industry-context"
            role="tabpanel"
            aria-live="polite"
          >
            <div>
              <h2>{activeIndustryOption?.label || '全部行业'}</h2>
            </div>
            <p>
              {activeIndustryOption?.description || '浏览云作 Studio 当前公开的全部行业案例，再按具体行业缩小范围。'}
            </p>
            <strong>{filteredWorks.length} 项案例</strong>
          </div>

          {worksLoading ? (
            <div className="studio-industry-skeleton" aria-label="正在加载行业案例">
              {Array.from({ length: 8 }).map((_, index) => (
                <span key={index} />
              ))}
            </div>
          ) : filteredWorks.length === 0 ? (
            <div className="studio-industry-empty">
              <p>该行业暂时还没有公开案例。</p>
              <span>官方作品发布时选择行业后，会自动归档到这里。</span>
              <button type="button" onClick={() => setActiveIndustry('all')}>
                浏览全部案例
                <ArrowRight aria-hidden="true" />
              </button>
            </div>
          ) : (
            <div className="studio-case-grid" role="list">
              {filteredWorks.map((work) => (
                <div
                  key={work.id}
                  className="studio-case-work"
                  role="listitem"
                >
                  <WorkCard
                    work={work}
                    variant="profile"
                    showEngagementStats={false}
                    onClick={() => void handleWorkClick(work)}
                  />
                </div>
              ))}
            </div>
          )}
        </section>
      </main>

      {selectedWork && (
        <WorkDetailModal
          work={selectedWork}
          isOpen={!!selectedWork}
          onClose={() => setSelectedWork(null)}
          isOwnProfile={false}
          displayMode="showcase"
        />
      )}
    </div>
  );
};

export default StudioIndustries;
